import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { Subject, takeUntil } from 'rxjs';
import {
  deletePermission,
  updatePermissionListFilter,
} from '../../store/permission-list.actions';
import { selectPermissionList } from '../../store/permission-list.selector';
import { PermissionListInterface } from '../../types/permission-list';
import { PermissionListFilter } from '../../types/permission-list-filter';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { PermissionService } from '../../services/permission.service';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';

@Component({
  selector: 'app-permission-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
    HasAccessDirective,
    InputModule,
  ],
  templateUrl: './permission-list.component.html',
  styleUrl: './permission-list.component.scss',
})
export class PermissionListComponent implements OnInit, OnDestroy {
  panelOpenState = false;
  displayedColumns: string[] = [
    'title',
    'slug',
    'permission_type',
    'display_in_menu',
  ];
  permissionData!: PermissionListInterface[];
  page = 0;
  total = 0;
  limit = 10;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private permissionService: PermissionService,
    private messageHandlerService: MessageHandlerService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.loadData();
    this.dispatchFilter();
  }

  loadData(): void {
    this.store
      .select(selectPermissionList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data.rawData) {
          this.permissionData = data.rawData;
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  dispatchFilter(permissionListFilter?: PermissionListFilter): void {
    this.store.dispatch(
      updatePermissionListFilter({ filter: permissionListFilter ?? {} })
    );
  }

  async onDelete(permissionId: string): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this permission?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.permissionService
        .deletePermission({ permission_section_id: permissionId })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.store.dispatch(
                deletePermission({ permission_section_id: permissionId })
              );
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
